import React, { useEffect, useMemo, useState } from "react";

import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Typography } from "antd";
import i18next from "i18next";
import { toast } from "react-toastify";

import { BSCoreServicesDtoMarketplaceLogisticDataDto } from "api/reactQuery/querySchemas";
import { getDistributorLogisticsData, getMpDistributors } from "components/Actions/distributorsActions";
import DistributorSelector from "components/DistributorSelector";
import Loader from "components/Loader";
import { dateTimeFormat } from "components/Utils/dayjsUtils";
import { getGridDefaultProps } from "constants/agGridConstants";
import { dateTimeFormatter } from "helpers/agGrid/valueFormatters";

const LogisticsInfo: React.FC = () => {
  const [distributors, setDistributors] = useState<Distributor[]>();
  const [isLoadingDistributors, setIsLoadingDistributors] = useState(true);
  const [selectedDistributorId, setSelectedDistributorId] = useState<Distributor["id"]>();

  const [LogisticsData, setLogisticsData] = useState<BSCoreServicesDtoMarketplaceLogisticDataDto[]>();
  const [isLoadingLogisticsData, setIsLoadingLogisticsData] = useState(false);

  useEffect(() => {
    setIsLoadingDistributors(true);

    getMpDistributors((response) =>
      setDistributors(response),
      (error) => toast.error(error.response.statusText ?? "Error"),
      () => setIsLoadingDistributors(false)
    );
  }, []);

  useEffect(() => {
    if (!selectedDistributorId) return;

    setIsLoadingLogisticsData(true);

    getDistributorLogisticsData(
      selectedDistributorId,
      (response: BSCoreServicesDtoMarketplaceLogisticDataDto[]) => setLogisticsData(response),
      (error) => toast.error(error.response.statusText ?? "Error"),
      () => setIsLoadingLogisticsData(false)
    );
  }, [selectedDistributorId]);

  const columnDefs = useMemo<ColDef<BSCoreServicesDtoMarketplaceLogisticDataDto>[]>(() => [
    { field: "cid", headerName: i18next.t("id") },
    { field: "distributorName", headerName: i18next.t("distributor_name") },
    { field: "depotName", headerName: i18next.t("warehouse_name") },
    { field: "deliveryETA", headerName: i18next.t("delivery_eta"), valueFormatter: (params) => dateTimeFormatter(params, dateTimeFormat) },
    { field: "tripType", headerName: i18next.t("trip_type") },
    { field: "companyName", headerName: i18next.t("shipping_carrier") },
    { field: "truckRegistrationNumber", headerName: i18next.t("truck_registration_number") },
  ],
    []
  );

  return (
    <>
      <Loader background="semiLight" isLoading={(isLoadingLogisticsData && !!selectedDistributorId) || isLoadingDistributors} />

      <Typography.Title level={3} style={{ marginBottom: 20 }}>
        {i18next.t("logistics_information")}
      </Typography.Title>

      <DistributorSelector
        distributors={distributors}
        isLoading={isLoadingDistributors}
        setSelectedDistributorId={setSelectedDistributorId}
      />

      {LogisticsData?.length > 0 && (
        <div style={{ height: 520, marginTop: 32 }}>
          <AgGridReact
            rowData={LogisticsData}
            columnDefs={columnDefs}
            {...getGridDefaultProps()}
          />
        </div>
      )}
    </>
  );
};
export default LogisticsInfo;
